'use client';

import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  IconButton,
  Input,
  Tooltip,
  createSelectListCollection,
  Checkbox,
  Popover,
  Button,
  Collapsible,
  Badge,
  RadioGroup,
} from '@terra/ui-design-system';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  ArrowUpDownIcon,
  SourceCodeIcon,
  FilterIcon,
  SlidersHorizontalIcon,
  Delete01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import {
  useTabScopedRequestsFilters,
  COMMON_STATIC_EXTENSIONS,
  hasActiveAdvancedRequestsFilters,
  hasActiveRequestsFilters,
  hasActiveSimpleRequestsFilters,
  type MethodFilter,
  type StatusFilter,
  type MimeTypeFilter,
  type ExtensionFilterMode,
  type SourceFilter,
  type SourceFilterValue,
} from '../../store/tab/requestsTabScopedFilterStore';
import { useFilterPresetsStore } from '../../store/filterPresetsStore';
import {
  TqlFilterInput,
  FilterPresetsManager,
  QuickSavePreset,
  ExclusionRulesManager,
} from '../tql';
import { ClearHistoryConfirmDialog } from '../dialogs/ClearHistoryConfirmDialog';
import { MultiSelectFilter } from '../filters/MultiSelectFilter';
import { useTranslations } from 'next-intl';

interface RequestsFilterBarProps {
  /** Callback to clear request history - optional for simpler use cases */
  onClearHistory?: () => Promise<void> | void;
  /** Total request count to display */
  requestCount?: number;
  /** Whether to show only in-scope requests */
  showOnlyInScope?: boolean;
  /** Callback when in-scope filter changes */
  onShowOnlyInScopeChange?: (checked: boolean) => void;
  /** Show the scope filter and clear history button (default: true for GlobalRequestsTab) */
  showScopeControls?: boolean;
  // TQL filter support
  tqlQuery?: string;
  setTqlQuery?: (query: string) => void;
  filterMode?: 'simple' | 'advanced';
  setFilterMode?: (mode: 'simple' | 'advanced') => void;
  sourceFilter?: SourceFilter;
  onSourceFilterChange?: (source: SourceFilter) => void;
}
const SOURCE_FILTER_VALUES: SourceFilterValue[] = [
  'proxy',
  'copilot',
  'neo',
  'replay',
];

function isSourceFilterValue(value: string): value is SourceFilterValue {
  return SOURCE_FILTER_VALUES.includes(value as SourceFilterValue);
}

function toSourceFilter(values: string[]): SourceFilter {
  return values.filter(isSourceFilterValue);
}

export function RequestsFilterBar({
  onClearHistory,
  requestCount,
  showOnlyInScope = false,
  onShowOnlyInScopeChange,
  showScopeControls = true,
  tqlQuery: tqlQueryProp,
  setTqlQuery: setTqlQueryProp,
  filterMode: filterModeProp,
  setFilterMode: setFilterModeProp,
  sourceFilter,
  onSourceFilterChange,
}: RequestsFilterBarProps) {
  const tCommon = useTranslations('common');
  const tFilters = useTranslations('httpHistory.filters');
  const filterBarRef = useRef<HTMLDivElement>(null);
  const tqlDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [availableWidth, setAvailableWidth] = useState<number>(1000); // Default to wide
  const [isClearHistoryDialogOpen, setIsClearHistoryDialogOpen] =
    useState(false);
  // Get all filter state from store (including persisted filterMode and tqlQuery)
  const {
    searchTerm,
    setSearchTerm,
    methodFilter,
    setMethodFilter,
    statusFilter,
    setStatusFilter,
    mimeTypeFilter,
    setMimeTypeFilter,
    extensionFilterMode,
    setExtensionFilterMode,
    selectedExtensions,
    toggleSelectedExtension,
    setSelectedExtensions,
    selectAllStaticExtensions,
    showAdvancedFilters,
    setShowAdvancedFilters,
    resetFilters,
    filterMode: storeFilterMode,
    setFilterMode: storeSetFilterMode,
    tqlQuery: storeTqlQuery,
    setTqlQuery: storeSetTqlQuery,
  } = useTabScopedRequestsFilters();
  const activePresetQuery = useFilterPresetsStore((state) => state.activeQuery);
  const clearActivePresetQuery = useFilterPresetsStore(
    (state) => state.setActiveQuery,
  );
  // Use prop values if provided, otherwise use persisted store values
  const filterMode = filterModeProp ?? storeFilterMode;
  const setFilterMode = setFilterModeProp ?? storeSetFilterMode;
  const tqlQuery = tqlQueryProp ?? storeTqlQuery;
  const [localTqlQuery, setLocalTqlQuery] = useState(tqlQuery);
  // Handle TQL query change - use prop setter if provided, otherwise use store
  const setTqlQuery = setTqlQueryProp ?? storeSetTqlQuery;

  useEffect(() => {
    setLocalTqlQuery(tqlQuery);
  }, [tqlQuery]);
  useEffect(() => {
    return () => {
      if (tqlDebounceRef.current) {
        clearTimeout(tqlDebounceRef.current);
      }
    };
  }, []);
  const applyTqlQueryImmediately = useCallback(
    (query: string) => {
      if (tqlDebounceRef.current) {
        clearTimeout(tqlDebounceRef.current);
      }

      setTqlQuery(query);
    },
    [setTqlQuery],
  );
  const handleTqlChange = useCallback(
    (query: string) => {
      setLocalTqlQuery(query);
      if (tqlDebounceRef.current) {
        clearTimeout(tqlDebounceRef.current);
      }

      tqlDebounceRef.current = setTimeout(() => {
        setTqlQuery(query);
      }, 120);
    },
    [setTqlQuery],
  );
  // Apply a preset to the filter
  const handleApplyPreset = useCallback(
    (query: string) => {
      setLocalTqlQuery(query);
      applyTqlQueryImmediately(query);
      setFilterMode('advanced');
    },
    [applyTqlQueryImmediately, setFilterMode],
  );

  useEffect(() => {
    const filterBar = filterBarRef.current;

    if (!filterBar) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) {
        setAvailableWidth(entry.contentRect.width);
      }
    });

    observer.observe(filterBar);

    return () => {
      observer.disconnect();
    };
  }, []);
  // Determine which filters are visible based on available width
  // Priority order (highest to lowest):
  // 1. Count + Search (always visible)
  // 2. Method filter (visible > 480px)
  // 3. Status filter (visible > 580px)
  // 4. Sort field (visible > 700px)
  // 5. Sort order toggle (visible > 780px)
  // 6. In-scope checkbox text (hidden < 650px, show icon only)
  // 7. In-scope checkbox (moved to More menu < 550px)
  const visibleFilters = useMemo(() => {
    return {
      requestCount: availableWidth > 520,
      source: availableWidth > 760,
      method: availableWidth > 900,
      status: availableWidth > 1020,
      inScopeCheckbox: false,
      inScopeText: false,
      actions: availableWidth > 700,
    };
  }, [availableWidth]);
  // Count active hidden primary filters
  const hiddenPrimaryFilterCount = [
    !visibleFilters.source && sourceFilter && sourceFilter.length > 0 ? 1 : 0,
    !visibleFilters.method && methodFilter.length > 0 ? 1 : 0,
    !visibleFilters.status && statusFilter.length > 0 ? 1 : 0,
    !visibleFilters.inScopeCheckbox && showOnlyInScope ? 1 : 0,
  ].reduce((a, b) => a + b, 0);
  // Count active advanced filters
  const advancedFilterCount = [
    mimeTypeFilter.length > 0,
    selectedExtensions.length > 0,
  ].filter(Boolean).length;
  // Total count for "More" badge (hidden primary + advanced)
  const totalMoreFilterCount = hiddenPrimaryFilterCount + advancedFilterCount;
  const effectiveAdvancedQuery = activePresetQuery || tqlQuery;
  const hasActiveSimpleFilters = hasActiveSimpleRequestsFilters({
    searchTerm,
    methodFilter,
    statusFilter,
    mimeTypeFilter,
    selectedExtensions,
  });
  const hasAllStaticExtensionsSelected = useMemo(
    () =>
      COMMON_STATIC_EXTENSIONS.every((extension) =>
        selectedExtensions.includes(extension),
      ),
    [selectedExtensions],
  );
  const hasActiveAdvancedFilters = hasActiveAdvancedRequestsFilters({
    tqlQuery: effectiveAdvancedQuery,
  });
  const hasActiveFilters = hasActiveRequestsFilters({
    searchTerm,
    methodFilter,
    statusFilter,
    mimeTypeFilter,
    selectedExtensions,
    tqlQuery: effectiveAdvancedQuery,
    filterMode,
  });
  const handleClearFilters = () => {
    resetFilters();
    if (filterMode === 'advanced') {
      clearActivePresetQuery('');
      setLocalTqlQuery('');
      applyTqlQueryImmediately('');

      return;
    }
  };

  const handleToggleSelectAllExtensions = useCallback(() => {
    if (hasAllStaticExtensionsSelected) {
      setSelectedExtensions([]);

      return;
    }

    selectAllStaticExtensions();
  }, [
    hasAllStaticExtensionsSelected,
    selectAllStaticExtensions,
    setSelectedExtensions,
  ]);
  const handleOpenClearHistoryDialog = useCallback(() => {
    setIsClearHistoryDialogOpen(true);
  }, []);
  const handleClearHistoryDialogOpenChange = useCallback((open: boolean) => {
    setIsClearHistoryDialogOpen(open);
  }, []);
  // Create collections for select dropdowns
  const methodCollection = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { label: 'GET', value: 'GET' },
          { label: 'POST', value: 'POST' },
          { label: 'PUT', value: 'PUT' },
          { label: 'DELETE', value: 'DELETE' },
          { label: 'PATCH', value: 'PATCH' },
        ],
      }),
    [],
  );
  const statusCollection = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { label: '2xx', value: '2xx' },
          { label: '3xx', value: '3xx' },
          { label: '4xx', value: '4xx' },
          { label: '5xx', value: '5xx' },
        ],
      }),
    [],
  );
  const mimeTypeCollection = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { label: 'HTML', value: 'html' },
          { label: 'JSON', value: 'json' },
          { label: tFilters('script'), value: 'script' },
          { label: 'CSS', value: 'css' },
          { label: tFilters('image'), value: 'image' },
          { label: tCommon('other'), value: 'other' },
        ],
      }),
    [tCommon, tFilters],
  );
  const sourceCollection = useMemo(
    () =>
      createSelectListCollection({
        items: [
          { label: 'Proxy', value: 'proxy' },
          { label: 'Replay', value: 'replay' },
          { label: 'Copilot', value: 'copilot' },
          { label: 'Neo', value: 'neo' },
        ],
      }),
    [],
  );

  return (
    <Box
      ref={filterBarRef}
      w="full"
      px="1"
      bg="bg.canvas"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      minW="0"
      overflow="visible"
      position="relative"
      zIndex={2}
    >
      <Box
        display="grid"
        gridTemplateColumns="auto minmax(0, 1fr) auto auto auto"
        gap="1"
        alignItems="center"
        h="31px"
        minW="0"
        w="full"
        maxW="full"
        overflow="visible"
      >
        {/* Left side - Request count & Filter mode toggle */}
        <HStack gap="1" alignItems="center" flexShrink={0} minW="0">
          {requestCount !== undefined && visibleFilters.requestCount && (
            <>
              <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                {tFilters.rich('requestCount', {
                  count: requestCount,
                  num: (chunks) => (
                    <span
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: `${String(requestCount).length + 1}ch`,
                      }}
                    >
                      {chunks}
                    </span>
                  ),
                })}
              </Text>
              <Box h="4" w="1px" bg="border.default" flexShrink={0} />
            </>
          )}

          {/* Filter Mode Toggle */}
          <HStack
            gap="0"
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
            overflow="hidden"
            flexShrink={0}
          >
            <Tooltip title={tFilters('simpleFilters')}>
              <Box position="relative">
                <Box
                  as="button"
                  aria-label={tFilters('simpleFilters')}
                  onClick={() => setFilterMode('simple')}
                  h="6"
                  w="6"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  bg={filterMode === 'simple' ? 'bg.muted' : 'transparent'}
                  color={filterMode === 'simple' ? 'fg.default' : 'fg.muted'}
                  _hover={{
                    bg: filterMode === 'simple' ? 'bg.muted' : 'bg.hover',
                  }}
                >
                  <SlidersHorizontalIcon size={12} />
                </Box>
                {filterMode !== 'simple' && hasActiveSimpleFilters && (
                  <Box
                    position="absolute"
                    top="0"
                    right="0"
                    w="1.5"
                    h="1.5"
                    borderRadius="full"
                    bg="fg.default"
                  />
                )}
              </Box>
            </Tooltip>
            <Tooltip title={tFilters('tqlQueryTooltip')}>
              <Box position="relative">
                <Box
                  as="button"
                  aria-label={tFilters('tqlQuery')}
                  onClick={() => setFilterMode('advanced')}
                  h="6"
                  w="6"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  bg={filterMode === 'advanced' ? 'bg.muted' : 'transparent'}
                  color={filterMode === 'advanced' ? 'fg.default' : 'fg.muted'}
                  _hover={{
                    bg: filterMode === 'advanced' ? 'bg.muted' : 'bg.hover',
                  }}
                >
                  <SourceCodeIcon size={12} />
                </Box>
                {filterMode !== 'advanced' && hasActiveAdvancedFilters && (
                  <Box
                    position="absolute"
                    top="0"
                    right="0"
                    w="1.5"
                    h="1.5"
                    borderRadius="full"
                    bg="fg.default"
                  />
                )}
              </Box>
            </Tooltip>
          </HStack>
        </HStack>

        {/* Center section - fills remaining space, input is centered within */}
        <Box display="flex" justifyContent="center" mx="1" minW="0" maxW="full">
          {filterMode === 'simple' ? (
            <Box position="relative" w="full" maxW="420px" minW="0">
              <Input
                placeholder={tFilters('searchUrlPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="xs"
                h="6"
                fontSize="xs"
              />
            </Box>
          ) : (
            <HStack gap="1" w="full" maxW="550px" minW="0" overflow="visible">
              <Box flex="1" minW="0">
                <TqlFilterInput
                  value={localTqlQuery}
                  onChange={handleTqlChange}
                  placeholder={tFilters('tqlPlaceholder')}
                  compact
                />
              </Box>
              {availableWidth > 760 && <QuickSavePreset />}
              {availableWidth > 820 && (
                <FilterPresetsManager onApplyPreset={handleApplyPreset} />
              )}
            </HStack>
          )}
        </Box>

        <HStack gap="1" alignItems="center" flexShrink={0} minW="0">
          {onSourceFilterChange && sourceFilter && visibleFilters.source && (
            <MultiSelectFilter
              collection={sourceCollection}
              values={sourceFilter}
              onValueChange={(values) =>
                onSourceFilterChange(toSourceFilter(values))
              }
              placeholder={tFilters('source')}
              minW="90px"
            />
          )}

          {filterMode === 'simple' && visibleFilters.method && (
            <MultiSelectFilter
              collection={methodCollection}
              values={methodFilter}
              onValueChange={(values) =>
                setMethodFilter(values as MethodFilter)
              }
              placeholder={tFilters('method')}
              minW="80px"
            />
          )}

          {filterMode === 'simple' && visibleFilters.status && (
            <MultiSelectFilter
              collection={statusCollection}
              values={statusFilter}
              onValueChange={(values) =>
                setStatusFilter(values as StatusFilter)
              }
              placeholder={tFilters('status')}
              minW="70px"
            />
          )}
        </HStack>

        <HStack gap="1" alignItems="center" flexShrink={0} minW="0">
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            h="6"
            flexShrink={0}
            px="1.5"
          >
            <HStack gap="1" alignItems="center">
              <FilterIcon size={10} />
              <Text fontSize="xs" hideBelow="sm">
                {tFilters('more')}
              </Text>
              {showAdvancedFilters ? (
                <ArrowUp01Icon size={10} />
              ) : (
                <ArrowDown01Icon size={10} />
              )}
              {totalMoreFilterCount > 0 && (
                <Badge size="sm" variant="fill">
                  {totalMoreFilterCount}
                </Badge>
              )}
            </HStack>
          </Button>
        </HStack>

        <HStack gap="1" alignItems="center" flexShrink={0} minW="0">
          {visibleFilters.actions && (
            <>
              <Tooltip title={tFilters('clearFilters')}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={handleClearFilters}
                  aria-label={tFilters('clearFilters')}
                  h="6"
                  minW="6"
                  disabled={!hasActiveFilters}
                >
                  <Cancel01Icon size={12} />
                </IconButton>
              </Tooltip>

              {showScopeControls && <ExclusionRulesManager />}

              {onClearHistory && (
                <Tooltip title={tFilters('clearHistory')}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={handleOpenClearHistoryDialog}
                    aria-label={tFilters('clearHistory')}
                    h="6"
                    minW="6"
                  >
                    <Delete01Icon size={12} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </HStack>
      </Box>

      {/* Advanced Filters (Collapsible) */}
      <Collapsible.Root
        open={showAdvancedFilters}
        onOpenChange={(details) => setShowAdvancedFilters(details.open)}
      >
        <Collapsible.Content>
          <Box
            p="2"
            bg="bg.canvas"
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            mt="1"
          >
            <HStack gap="2" alignItems="flex-start" flexWrap="wrap">
              {onSourceFilterChange &&
                sourceFilter &&
                !visibleFilters.source && (
                  <VStack gap="1" alignItems="flex-start">
                    <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                      {tFilters('source')}
                    </Text>
                    <MultiSelectFilter
                      collection={sourceCollection}
                      values={sourceFilter}
                      onValueChange={(values) =>
                        onSourceFilterChange(toSourceFilter(values))
                      }
                      placeholder={tFilters('source')}
                      minW="100px"
                    />
                  </VStack>
                )}

              {/* Hidden Method Filter */}
              {filterMode === 'simple' && !visibleFilters.method && (
                <VStack gap="1" alignItems="flex-start">
                  <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                    {tFilters('method')}
                  </Text>
                  <MultiSelectFilter
                    collection={methodCollection}
                    values={methodFilter}
                    onValueChange={(values) =>
                      setMethodFilter(values as MethodFilter)
                    }
                    placeholder={tFilters('method')}
                    minW="100px"
                  />
                </VStack>
              )}

              {/* Hidden Status Filter */}
              {filterMode === 'simple' && !visibleFilters.status && (
                <VStack gap="1" alignItems="flex-start">
                  <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                    {tFilters('status')}
                  </Text>
                  <MultiSelectFilter
                    collection={statusCollection}
                    values={statusFilter}
                    onValueChange={(values) =>
                      setStatusFilter(values as StatusFilter)
                    }
                    placeholder={tFilters('status')}
                    minW="100px"
                  />
                </VStack>
              )}

              {/* Hidden In-Scope Checkbox */}
              {showScopeControls &&
                onShowOnlyInScopeChange &&
                (!visibleFilters.actions ||
                  !visibleFilters.inScopeCheckbox) && (
                  <VStack gap="1" alignItems="flex-start">
                    <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                      {tFilters('scope')}
                    </Text>
                    <Checkbox
                      size="sm"
                      checked={showOnlyInScope}
                      onCheckedChange={(details) =>
                        onShowOnlyInScopeChange(details.checked === true)
                      }
                    >
                      <Text fontSize="xs">{tFilters('inScopeOnly')}</Text>
                    </Checkbox>
                  </VStack>
                )}

              {/* MIME Type Filter */}
              <VStack gap="1" alignItems="flex-start">
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                  {tFilters('mimeType')}
                </Text>
                <MultiSelectFilter
                  collection={mimeTypeCollection}
                  values={mimeTypeFilter}
                  onValueChange={(values) =>
                    setMimeTypeFilter(values as MimeTypeFilter)
                  }
                  placeholder={tFilters('mimeType')}
                  minW="100px"
                />
              </VStack>

              {/* Extension Filter */}
              <VStack gap="1" alignItems="flex-start">
                <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                  {tFilters('extensions')}
                </Text>
                <HStack gap="2" alignItems="center">
                  {/* Mode Toggle */}
                  <RadioGroup.Root
                    value={extensionFilterMode}
                    onValueChange={(details) =>
                      setExtensionFilterMode(
                        details.value as ExtensionFilterMode,
                      )
                    }
                    size="sm"
                  >
                    <HStack gap="2">
                      <RadioGroup.Item value="hide">
                        <RadioGroup.ItemControl />
                        <RadioGroup.ItemText>
                          <Text fontSize="xs">{tFilters('hide')}</Text>
                        </RadioGroup.ItemText>
                        <RadioGroup.ItemHiddenInput />
                      </RadioGroup.Item>
                      <RadioGroup.Item value="show_only">
                        <RadioGroup.ItemControl />
                        <RadioGroup.ItemText>
                          <Text fontSize="xs">{tFilters('showOnly')}</Text>
                        </RadioGroup.ItemText>
                        <RadioGroup.ItemHiddenInput />
                      </RadioGroup.Item>
                    </HStack>
                  </RadioGroup.Root>

                  {/* Extension Selector */}
                  <Popover.Root positioning={{ strategy: 'fixed' }}>
                    <Popover.Trigger asChild>
                      <Button
                        variant="outline"
                        size="xs"
                        h="6"
                        minW="100px"
                        justifyContent="space-between"
                        fontWeight="normal"
                        color={
                          selectedExtensions.length > 0
                            ? 'fg.default'
                            : 'fg.muted'
                        }
                      >
                        {selectedExtensions.length > 0
                          ? tCommon('selectedCount', {
                              count: selectedExtensions.length,
                            })
                          : tCommon('selectPlaceholder')}
                        <ArrowUpDownIcon size={8} />
                      </Button>
                    </Popover.Trigger>
                    <Popover.Positioner>
                      <Popover.Content
                        p="2"
                        minW="160px"
                        maxH="250px"
                        overflowY="auto"
                      >
                        <VStack gap="1" alignItems="flex-start">
                          <Checkbox
                            size="sm"
                            checked={hasAllStaticExtensionsSelected}
                            onCheckedChange={handleToggleSelectAllExtensions}
                          >
                            <Text fontSize="xs">
                              {hasAllStaticExtensionsSelected
                                ? tCommon('deselectAll')
                                : tFilters('selectAllExtensions')}
                            </Text>
                          </Checkbox>
                          {COMMON_STATIC_EXTENSIONS.map((ext) => (
                            <Checkbox
                              key={ext}
                              size="sm"
                              checked={selectedExtensions.includes(ext)}
                              onCheckedChange={() =>
                                toggleSelectedExtension(ext)
                              }
                            >
                              <Text fontSize="xs">{ext}</Text>
                            </Checkbox>
                          ))}
                        </VStack>
                      </Popover.Content>
                    </Popover.Positioner>
                  </Popover.Root>
                </HStack>
              </VStack>

              {!visibleFilters.actions && (
                <VStack gap="1" alignItems="flex-start">
                  <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                    {tFilters('actions')}
                  </Text>
                  <HStack gap="1" alignItems="center" flexWrap="wrap">
                    <Tooltip title={tFilters('clearFilters')}>
                      <IconButton
                        variant="ghost"
                        size="xs"
                        onClick={handleClearFilters}
                        aria-label={tFilters('clearFilters')}
                        h="6"
                        minW="6"
                        disabled={!hasActiveFilters}
                      >
                        <Cancel01Icon size={12} />
                      </IconButton>
                    </Tooltip>
                    {showScopeControls && <ExclusionRulesManager />}
                    {onClearHistory && (
                      <Tooltip title={tFilters('clearHistory')}>
                        <IconButton
                          variant="ghost"
                          size="xs"
                          onClick={handleOpenClearHistoryDialog}
                          aria-label={tFilters('clearHistory')}
                          h="6"
                          minW="6"
                        >
                          <Delete01Icon size={12} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </HStack>
                </VStack>
              )}
            </HStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>

      {onClearHistory && (
        <ClearHistoryConfirmDialog
          isOpen={isClearHistoryDialogOpen}
          onOpenChange={handleClearHistoryDialogOpenChange}
          onConfirm={onClearHistory}
        />
      )}
    </Box>
  );
}
